'use client';

/* eslint-disable jsx-a11y/label-has-associated-control */
import type { HTMLAttributes, ReactNode } from 'react';
import React from 'react';
import classNames from 'classnames';
import { Locale } from '@fixter/i18n';
import { useAppContext } from '$util/AppContext';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { DropDown } from '$src-components/atoms/DropDown';
import { Icon } from '@/components/atoms/Icon';
import { getCmsMenuItems } from './cms';

import styles from './index.module.scss';

const localeHighlightLinks = new Map([[Locale.EN_GB, new Set(['navbar.link.seeAllRepairs'])]]);

export interface NavigationMenuProps extends HTMLAttributes<HTMLDivElement> {
  readonly footer?: ReactNode;
}

/**
 * @deprecated use from `src`
 */
export function NavigationMenu({
  id = 'nav-menu',
  className,
  children,
  footer,
  ...props
}: NavigationMenuProps): JSX.Element {
  const {
    cmsContent: { dictionary },
    locale,
  } = useAppContext<AppContextGlobal>();

  const menuItems = getCmsMenuItems(dictionary, locale);
  const highlightLinks = localeHighlightLinks.get(locale);

  return (
    <div
      id={id}
      className={classNames(NavigationMenu.displayName, styles.NavigationMenu, className)}
      {...props}
    >
      <input type="checkbox" id={`${id}-toggle`} />
      <label
        htmlFor={`${id}-toggle`}
        className={styles['menu-toggle']}
        onClickCapture={() => {
          /**
           * No scroll on mobile when menu is opened.
           */
          const overlayClassName = `${NavigationMenu.displayName}-overlay`;
          if (document.body.classList.contains(overlayClassName)) {
            document.body.classList.remove(overlayClassName);
          } else {
            document.body.classList.add(overlayClassName);
          }
        }}
      >
        <Icon className={styles.mobileMenu} id="general/menu-01" legacy={false} />
        <Icon className={styles.closeMobileMenu} id="general/x-close" legacy={false} />
      </label>
      <div className={classNames('menu', styles.menu)}>
        <ul>
          {menuItems.map((item) =>
            'links' in item ? (
              <li key={item.title}>
                <DropDown title={item.title}>
                  <ul>
                    {item.links.map((link) => (
                      <li
                        key={link.key}
                        className={classNames(highlightLinks?.has(link.key) && styles.highlight)}
                      >
                        <a href={link.url}>
                          <span>{link.text}</span>
                          {highlightLinks?.has(link.key) && (
                            <>
                              <Icon id="arrows/arrow-right" width={16} height={16} legacy={false} />
                              <Icon id="arrows/arrow-up-right" width={16} height={16} legacy={false} />
                            </>
                          )}
                        </a>
                      </li>
                    ))}
                  </ul>
                </DropDown>
              </li>
            ) : (
              <li key={item.key}>
                <a href={item.url}>
                  <span>{item.text}</span>
                  <Icon id="arrows/arrow-right" legacy={false} />
                </a>
              </li>
            )
          )}
          {children}
        </ul>

        {footer}
      </div>
    </div>
  );
}
NavigationMenu.displayName = 'NavigationMenu';
